import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";

import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { ClientName } from "libs/config/client.names";
import { WebviewerVisibilityScope } from "libs/treatments/src/lib/models/treatment.model";

import { CreateWebviewerDto, WebviewerDocument, UpdateWebviewerDto } from "../models/webviewer-tms-api.model";
import { WebViewerModificationDataContainer, WebviewerCreatedByRole } from "../models/webviewer-data.model";

@Injectable({
	providedIn: "root"
})
export class WebviewerModificationService {
	private readonly http = inject(HttpClient);
	private readonly config = inject<AppConfig>(APP_CONFIG_TOKEN);

	/**
	 * Create a new webviewer document
	 */
	create(createDto: CreateWebviewerDto): Observable<WebviewerDocument> {
		return this.http.post<WebviewerDocument>(`${this.config.apiUrl}/webviewer`, createDto, { withCredentials: true });
	}

	/**
	 * Get all webviewer documents for a customer
	 */
	findAll(customerWmsId?: string): Observable<WebviewerDocument[]> {
		const url = `${this.config.apiUrl}/webviewer`;
		const queryParams = customerWmsId ? { customerWmsId } : {};
		const options = { params: queryParams, withCredentials: true };
		return this.http.get<WebviewerDocument[]>(url, options);
	}

	/**
	 * Get a webviewer document by ID
	 */
	findOne(id: string): Observable<WebviewerDocument | null> {
		return this.http.get<WebviewerDocument | null>(`${this.config.apiUrl}/webviewer/${id}`, { withCredentials: true });
	}

	/**
	 * Get multiple webviewer documents by IDs
	 */
	findMany(ids: string[]): Observable<WebviewerDocument[]> {
		if (ids.length === 0) {
			return of([]);
		}
		return this.http.get<WebviewerDocument[]>(`${this.config.apiUrl}/webviewer/many`, {
			params: { "ids[]": ids },
			withCredentials: true
		});
	}

	/**
	 * Update a webviewer document
	 */
	update(
		id: string,
		updateDto: UpdateWebviewerDto
	): Observable<WebviewerDocument | null> {
		return this.http.patch<WebviewerDocument | null>(`${this.config.apiUrl}/webviewer/${id}`, updateDto, {
			withCredentials: true
		});
	}

	/**
	 * Delete a webviewer document
	 */
	remove(id: string): Observable<WebviewerDocument | null> {
		return this.http.delete<WebviewerDocument | null>(`${this.config.apiUrl}/webviewer/${id}`, { withCredentials: true });
	}

	/**
	 * Create a webviewer document from WebViewerModificationDataContainer received from the webviewer app.
	 */
	createFromModificationData(
		modificationData: WebViewerModificationDataContainer,
		customerWmsId: string,
		visibilityScope?: WebviewerVisibilityScope
	): Observable<WebviewerDocument> {
		const createDto: CreateWebviewerDto = {
			version: modificationData.version,
			type: modificationData.type,
			data: {
				...modificationData.data,
				modification: {
					...modificationData.data.modification,
					markers: modificationData.data.modification.markers.map((marker) => ({
						...marker,
						createdByRole:
							marker.createdByRole ??
							(this.config.app === ClientName.TM_ADMIN
								? WebviewerCreatedByRole.TECHNICIAN
								: WebviewerCreatedByRole.DOCTOR),
						timestamp: marker.timestamp ?? new Date().toISOString(),
					})),
				},
			},
			meta: {
				...modificationData.meta,
				createdBy:
					modificationData.meta.createdBy ?? `${this.config.app} (${this.config.versionLabel})`,
				createdAt: modificationData.meta.createdAt ?? new Date().toISOString(),
				createdByRole:
					modificationData.meta.createdByRole ??
					(this.config.app === ClientName.TM_ADMIN
						? WebviewerCreatedByRole.TECHNICIAN
						: WebviewerCreatedByRole.DOCTOR),
			},
			customerWmsId: customerWmsId,
		};
		if (visibilityScope) {
			createDto.visibilityScope = visibilityScope;
		}
		return this.create(createDto);
	}
}
